import type { KeyboardEvent, MouseEvent } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import type { NavHeaderLinkItem, NavLinkItemValue } from '@/gql/schema'
import { resolveNavLink, type MegaMenuClasses } from '@/utils/navigation'
import { useLocalizedPath } from '@/hooks/useLocalizedPath'

type NavItem = Pick<NavHeaderLinkItem, 'link' | 'children' | 'label'>

export interface DesktopMegaMenuProps {
  items: NavItem[]
  orientation: 'vertical' | 'horizontal'
  classes: MegaMenuClasses
  onNavigate: (url: string, isExternal: boolean) => void
}

/**
 * Desktop navigation menu with hover / keyboard-focus dropdown panels.
 *
 * Keeps the `p-megamenu-*` / `p-menuitem*` class structure of the PrimeReact MegaMenu it
 * replaced, so the structural rules in index.css apply unchanged. Panels open on hover and on
 * :focus-within (CSS); Escape closes the one holding focus.
 */
export default function DesktopMegaMenu({ items, orientation, classes, onNavigate }: DesktopMegaMenuProps) {
  const SubmenuIcon = orientation === 'horizontal' ? ChevronDown : ChevronRight
  const lp = useLocalizedPath()

  const handleClick = (event: MouseEvent<HTMLAnchorElement>, target: NavLinkItemValue | NavItem['link']) => {
    // Let the browser handle new-tab / new-window clicks on the real href.
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    // Same as the PrimeReact MegaMenu this replaced: a real href is also followed by the
    // browser; only the '#' placeholder is kept from changing the URL.
    if (event.currentTarget.getAttribute('href') === '#') event.preventDefault()
    const { url, isExternal } = resolveNavLink(target)
    onNavigate(url, isExternal)
  }

  // Blurring drops :focus-within, which closes the panel the focused link belongs to.
  const handleKeyDown = (event: KeyboardEvent<HTMLAnchorElement>) => {
    if (event.key === 'Escape') event.currentTarget.blur()
  }

  return (
    <div className={`p-megamenu p-megamenu-${orientation} ${classes.root}`}>
      <ul className={`p-megamenu-root-list ${classes.menu}`}>
        {items.map((item) => {
          const children = item.children ?? []
          const hasChildren = children.length > 0

          return (
            <li key={item.label} className={`p-menuitem ${classes.menuitem}`}>
              <div className={`p-menuitem-content ${classes.content}`}>
                <a
                  href={item.link?.url ? lp(item.link.url) : '#'}
                  className={`p-menuitem-link ${classes.action}`}
                  aria-haspopup={hasChildren ? 'true' : undefined}
                  onClick={(e) => handleClick(e, item.link)}
                  onKeyDown={handleKeyDown}
                >
                  <span className={`p-menuitem-text ${classes.label}`}>{item.label}</span>
                  {hasChildren && (
                    <SubmenuIcon aria-hidden="true" className={`p-submenu-icon ${classes.submenuIcon}`} />
                  )}
                </a>
              </div>

              {hasChildren && (
                <div className={`p-megamenu-panel ${classes.panel}`}>
                  <div className={`p-megamenu-grid ${classes.grid}`}>
                    <div className={`p-megamenu-col-12 ${classes.column}`}>
                      <ul className={`p-submenu-list p-megamenu-submenu ${classes.submenu}`}>
                        {children.map((child) => (
                          <li key={child.label} className={`p-menuitem ${classes.submenuItem}`}>
                            <div className={`p-menuitem-content ${classes.content}`}>
                              <a
                                href={child.url ? lp(child.url) : '#'}
                                className={`p-menuitem-link ${classes.action}`}
                                onClick={(e) => handleClick(e, child)}
                                onKeyDown={handleKeyDown}
                              >
                                <span className={`p-menuitem-text ${classes.label}`}>{child.label}</span>
                              </a>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
